import '@fontsource-variable/geist-mono';
import type { User } from '@fleequid-secrets/shared';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, StrictMode, useCallback, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { api, ApiError, onApiError } from '@/api/client';
import { App } from '@/App';
import { ChoosePassword } from '@/components/ChoosePassword';
import { Login } from '@/components/Login';
import '@/styles/app.css';

const client = new QueryClient({
    defaultOptions: {
        queries: { retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2, refetchOnWindowFocus: false },
    },
});

// Sign-in, then one's own password if an admin set it, then the app. A session that ends anywhere brings sign-in back.
function Root(): ReactNode {
    const queryClient = useQueryClient();
    const me = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
    const signedIn = useCallback((user: User) => queryClient.setQueryData(['me'], { user }), [queryClient]);
    // Who is signed in goes first, on the query this component watches (clearing the cache first would orphan it and
    // leave the screen as it was); then whatever they had read.
    const signedOut = useCallback(() => {
        queryClient.setQueryData(['me'], null);
        queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
    }, [queryClient]);
    const signOut = useCallback(() => {
        void api.logout().finally(signedOut);
    }, [signedOut]);
    useEffect(() => {
        onApiError((error) => {
            if (error.status === 401 && queryClient.getQueryData(['me'])) {
                signedOut();
            } else if (error.code === 'password') {
                void queryClient.invalidateQueries({ queryKey: ['me'] });
            }
        });
    }, [queryClient, signedOut]);

    if (me.isLoading) {
        return null;
    }
    const user = me.data?.user;
    if (!user) {
        return <Login onSignedIn={signedIn} />;
    }
    if (user.mustChangePassword) {
        return <ChoosePassword user={user} onChosen={signedIn} onSignOut={signOut} />;
    }

    return <App user={user} onLogout={signOut} />;
}

createRoot(document.getElementById('root') as HTMLElement).render(
    <StrictMode>
        <QueryClientProvider client={client}>
            <Root />
        </QueryClientProvider>
    </StrictMode>,
);
